import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Event } from '../types';
import { legacyEvents } from '../data/legacyEvents';
import { fetchEventsList } from '../lib/api';
import { mapApiEventToEvent } from '../lib/mapEvent';

type EventsContextValue = {
  events: Event[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const EventsContext = createContext<EventsContextValue | undefined>(undefined);

export const EventsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [events, setEvents] = useState<Event[]>(legacyEvents);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchEventsList();
      const publishedLocalEvents = rows
        .filter((row) => String(row.id).startsWith('BFN-E-') && row.status === 'published')
        .map((row) => mapApiEventToEvent(row));
      setEvents([...publishedLocalEvents, ...legacyEvents]);
    } catch {
      // The static Bihar events remain available if the live API is temporarily unavailable.
      setEvents(legacyEvents);
      setError('Events could not be refreshed.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <EventsContext.Provider value={{ events, loading, error, refresh }}>
      {children}
    </EventsContext.Provider>
  );
};

export function useEvents(): EventsContextValue {
  const ctx = useContext(EventsContext);
  if (!ctx) {
    throw new Error('useEvents must be used within EventsProvider');
  }
  return ctx;
}
